import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { CheckCircle2, AlertTriangle, Database, Cpu, HardDrive, MemoryStick, Activity, Bug } from 'lucide-react';
import {
  useAdminData, Metric, LoadingBlock, ErrorBox, SectionNote, count,
} from '../../components/admin/AdminKit';

// WO-36 — real checks only. The old page showed "Healthy · 99.9%" typed into
// the code, so it could never turn red. Uptime history and error counts need an
// outside monitor and error tracking, and say "Not measured yet" until then.

const CHECK_LABELS = {
  database: 'Database',
  stripe_key: 'Stripe key',
  stripe_webhook_secret: 'Stripe webhook secret',
  email_key: 'E-mail (Resend) key',
};

export default function AdminSystemHealth() {
  const { data, loading, error, reload } = useAdminData('/api/analytics/system/health');
  if (loading) return <LoadingBlock testid="admin-health-loading" />;
  const ph = data?.platform_health || {};
  const infra = data?.infrastructure || {};
  const good = ph.status === 'ok';

  return (
    <div className="space-y-6" data-testid="admin-system-health">
      <div>
        <h1 className="text-2xl font-bold">System Health</h1>
        <p className="text-muted-foreground">Checked when you open this page</p>
      </div>
      {error && <ErrorBox message={error} onRetry={reload} />}
      {data && (
        <>
          <Card className={`border-0 ${good ? 'bg-green-500/10' : 'bg-amber-500/10'}`} data-testid="health-banner">
            <CardContent className="p-5 flex items-start gap-4">
              {good ? <CheckCircle2 className="w-8 h-8 text-green-500 shrink-0" /> : <AlertTriangle className="w-8 h-8 text-amber-500 shrink-0" />}
              <div className="flex-1">
                <p className="text-lg font-semibold">{good ? 'All checks passed' : 'Something needs attention'}</p>
                <div className="grid sm:grid-cols-2 gap-2 mt-3">
                  {Object.entries(ph.checks || {}).map(([key, c]) => (
                    <div key={key} className="flex items-center gap-2 text-sm">
                      {c.ok ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                      <span className="font-medium">{CHECK_LABELS[key] || key}</span>
                      <span className="text-muted-foreground">— {c.detail}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Metric label="Uptime history" icon={Activity} value={null}
              why="Needs an outside monitor that checks the app every few minutes" />
            <Metric label="Server errors, 24 h / 7 days" icon={Bug} value={null}
              why="Needs error tracking (Phase 2)" />
            <Metric label="AI cost" icon={Cpu} value={null} why="AI calls are not counted yet" />
          </div>

          <Card className="border-0 bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Database className="w-5 h-5 text-primary" />Database</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric label="Data size" value={`${infra.database?.size_mb ?? '—'} MB`} />
              <Metric label="On disk" value={`${infra.database?.storage_mb ?? '—'} MB`} />
              <Metric label="Collections" value={count(infra.database?.collections)} />
              <Metric label="Records" value={count(infra.database?.objects)} />
            </CardContent>
          </Card>

          <Card className="border-0 bg-card/50">
            <CardHeader>
              <CardTitle>Hosting machine</CardTitle>
              <CardDescription>
                This is the hosting provider's shared machine, not Mid2Bid alone — it moves with other customers' load
                and is not a measure of the app.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Metric label="CPU" icon={Cpu} value={`${infra.cpu_percent ?? '—'}%`} />
              <Metric label="Memory" icon={MemoryStick} value={`${infra.memory?.percent ?? '—'}%`}
                note={`${infra.memory?.used_gb} of ${infra.memory?.total_gb} GB`} />
              <Metric label="Disk" icon={HardDrive} value={`${infra.disk?.percent ?? '—'}%`}
                note={`${infra.disk?.used_gb} of ${infra.disk?.total_gb} GB`} />
            </CardContent>
          </Card>
          <SectionNote>Nothing on this page alerts anyone by itself; alerts by e-mail are part of Phase 2.</SectionNote>
        </>
      )}
    </div>
  );
}
